import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { AlertTriangle, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function ResetPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [confirmText, setConfirmText] = useState("");
  const [working, setWorking] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/reset/status");
      setItems(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const doReset = async () => {
    if (confirmText.trim().toUpperCase() !== "SIFIRLA") {
      toast.error('Onay için "SIFIRLA" yazmalısınız');
      return;
    }
    setWorking(true);
    try {
      const { data } = await api.post(`/admin/reset/${target.scope}`,
        { confirm: "SIFIRLA" });
      toast.success(`${data.label}: ${data.total} kayıt silindi`);
      setTarget(null);
      setConfirmText("");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sıfırlama başarısız");
    } finally { setWorking(false); }
  };

  return (
    <div className="space-y-5">
      <div className="azd-card p-5 border-l-4 border-[#DC2626] bg-[#FEF2F2]">
        <div className="flex items-start gap-3">
          <AlertTriangle size={22} className="text-[#DC2626] shrink-0 mt-0.5" />
          <div>
            <div className="font-extrabold text-[#7F1D1D] mb-1">Tehlikeli Bölge</div>
            <div className="text-sm text-[#991B1B]">
              Bu ekrandaki butonlar, ilgili modüldeki <b>TÜM kayıtları kalıcı olarak siler</b>.
              Bu işlemler geri alınamaz. Sadece test verilerini temizlemek için kullanın.
              Onay metni olarak <b>SIFIRLA</b> yazmanız istenecektir.
            </div>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="azd-card p-8 text-center text-[#6B7280]">
          <Loader2 size={22} className="animate-spin inline mr-2" /> Yükleniyor...
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((it) => (
            <div key={it.scope} data-testid={`reset-card-${it.scope}`}
                 className="azd-card p-4 flex flex-col">
              <div className="flex items-start justify-between mb-2">
                <div className="font-bold text-[#08285A]">{it.label}</div>
                <div className="text-xs px-2 py-0.5 rounded-full bg-[#F6F8FB] text-[#6B7280] font-bold">
                  {it.count}
                </div>
              </div>
              <div className="text-[11px] text-[#6B7280] mb-3">
                {it.collections.join(", ")}
              </div>
              <Button
                onClick={() => { setTarget(it); setConfirmText(""); }}
                disabled={it.count === 0}
                data-testid={`reset-btn-${it.scope}`}
                variant="outline"
                className="w-full mt-auto border-[#DC2626]/40 text-[#DC2626] hover:bg-[#DC2626]/10 hover:text-[#DC2626] disabled:opacity-50"
              >
                <Trash2 size={14} className="mr-1.5" />
                {it.count === 0 ? "Boş" : "Sıfırla"}
              </Button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!target} onOpenChange={(o) => !o && setTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#DC2626]">
              <AlertTriangle size={18} /> {target?.label} Sıfırlanacak
            </DialogTitle>
            <DialogDescription>
              Bu işlem geri alınamaz. Devam etmek için onay metnini yazın.
            </DialogDescription>
          </DialogHeader>
          {target && (
            <div className="space-y-3 py-2">
              <div className="p-3 rounded-lg bg-[#FEF2F2] border border-[#DC2626]/30 text-sm">
                <div className="font-bold text-[#7F1D1D] mb-1">Silinecek:</div>
                <div className="text-[#991B1B]">
                  <b>{target.label}</b> modülünden <b>{target.count}</b> kayıt kalıcı olarak silinecek.
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold mb-1.5 text-[#111827]">
                  Onaylamak için aşağıya <span className="font-mono font-extrabold text-[#DC2626]">SIFIRLA</span> yazın:
                </label>
                <Input
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="SIFIRLA"
                  autoFocus
                  data-testid="reset-confirm-input"
                  className="font-mono"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setTarget(null)}>Vazgeç</Button>
            <Button
              onClick={doReset}
              disabled={working || confirmText.trim().toUpperCase() !== "SIFIRLA"}
              data-testid="reset-confirm-btn"
              className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
            >
              {working ? "Siliniyor..." : "Kalıcı Olarak Sil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
